<template>
  <v-container>
    <div class="d-flex flex-wrap align-center ga-3 mb-4">
      <p class="text-body-medium text-medium-emphasis mb-0 flex-grow-1 danger-intro">
        Arbres, éboulis, rapides modifiés : les dangers signalés par la communauté. Vérifiez toujours sur place et
        repérez les rapides en cas de doute.
      </p>
      <v-btn v-if="isConnected" color="danger" variant="flat" prepend-icon="mdi-alert-plus" @click="openForm(null)">
        Signaler un danger
      </v-btn>
      <v-btn v-else color="danger" variant="tonal" prepend-icon="mdi-login" :to="{ name: 'connexion' }">
        Connectez-vous pour signaler un danger
      </v-btn>
    </div>

    <template v-if="loading">
      <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
    </template>
    <template v-else>
      <v-empty-state
        v-if="!active.length"
        icon="mdi-shield-check-outline"
        size="64"
        title="Aucun danger actif signalé"
        text="Personne n'a signalé de danger sur cette section. Si vous en voyez un, signalez-le pour les prochains."
      ></v-empty-state>
      <v-row v-else>
        <v-col v-for="danger in active" :key="danger.id" cols="12" lg="6">
          <danger-card
            :danger="danger"
            :can-checkup="isMember || isAdmin"
            :can-edit="canEdit(danger)"
            :can-flag="isConnected"
            @checkup="openCheckup"
            @edit="openForm"
            @flag="openFlag"
          ></danger-card>
        </v-col>
      </v-row>

      <v-expansion-panels v-if="archived.length" class="mt-6">
        <v-expansion-panel>
          <v-expansion-panel-title>
            <v-icon start>mdi-archive-outline</v-icon>
            Dangers archivés ({{ archived.length }})
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-row>
              <v-col v-for="danger in archived" :key="danger.id" cols="12" lg="6">
                <danger-card
                  :danger="danger"
                  :can-checkup="isMember || isAdmin"
                  :can-edit="canEdit(danger)"
                  :can-flag="isConnected"
                  @unarchive="unarchive"
                  @edit="openForm"
                  @flag="openFlag"
                ></danger-card>
              </v-col>
            </v-row>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>

    <danger-form
      v-model="formOpen"
      :section="section"
      :danger="editedDanger"
      :rapid-suggestions="rapidSuggestions"
      @saved="$emit('changed')"
    ></danger-form>

    <v-dialog v-model="checkupOpen" max-width="520" persistent>
      <v-card title="Vérification du danger" :subtitle="selectedDanger ? selectedDanger.rapid_name : ''">
        <v-card-text>
          <v-form ref="checkupForm" @submit.prevent="saveCheckup">
            <v-date-input
              :model-value="isoDateToDate(checkup.date)"
              label="Date de la vérification"
              :max="new Date()"
              :rules="[(value) => !!value || 'Requis']"
              @update:model-value="checkup.date = dateToIsoDate($event)"
            ></v-date-input>
            <v-textarea
              v-model="checkup.comment"
              label="Commentaire (optionnel)"
              rows="2"
              auto-grow
              :counter="1000"
              :rules="[(value) => !value || value.length <= 1000 || '1000 caractères maximum']"
            ></v-textarea>
            <v-checkbox
              v-model="checkup.archive"
              label="Archiver : le danger n'est plus là"
              color="primary"
              hide-details
            ></v-checkbox>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" :disabled="saving" @click="checkupOpen = false">Annuler</v-btn>
          <v-btn color="primary" variant="flat" :loading="saving" @click="saveCheckup">
            {{ checkup.archive ? "Vérifier et archiver" : "Toujours présent" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="flagOpen" max-width="520" persistent>
      <v-card title="Signaler aux administrateurs">
        <v-card-text>
          <p class="text-body-medium mb-4">
            Ce signalement vous semble erroné, inapproprié ou en double? Expliquez-nous pourquoi, un administrateur va
            le vérifier.
          </p>
          <v-form ref="flagForm" @submit.prevent="saveFlag">
            <v-textarea
              v-model="flagComment"
              label="Commentaire"
              rows="3"
              auto-grow
              :counter="1000"
              :rules="[
                (value) => !!(value || '').trim() || 'Requis',
                (value) => !value || value.length <= 1000 || '1000 caractères maximum',
              ]"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" :disabled="saving" @click="flagOpen = false">Annuler</v-btn>
          <v-btn color="primary" variant="flat" :loading="saving" @click="saveFlag">Envoyer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import { auth } from "@/firebase";
  import DangerCard from "@/components/pages/river/components/DangerCard.vue";
  import DangerForm from "@/components/pages/river/components/DangerForm.vue";
  import { DangersService } from "@/store/modules/dangers";
  import { notify, notifyError } from "@/services/Notification.service";
  import { dateToIsoDate, isoDateToDate } from "@/utils/dates";
  import { splitDangers } from "@/utils/dangers";

  export default {
    components: { DangerCard, DangerForm },
    props: {
      section: { type: Object, required: true },
      dangers: { type: Array, default: () => [] },
      loading: Boolean,
      isConnected: Boolean,
      isMember: Boolean,
      isAdmin: Boolean,
    },
    // changed: a danger was added or modified, the parent reloads the list.
    emits: ["changed"],
    data() {
      return {
        formOpen: false,
        editedDanger: null,
        selectedDanger: null,
        checkupOpen: false,
        checkup: { date: null, comment: "", archive: false },
        flagOpen: false,
        flagComment: "",
        saving: false,
      };
    },
    computed: {
      split() {
        return splitDangers(this.dangers);
      },
      active() {
        return this.split.active;
      },
      archived() {
        return this.split.archived;
      },
      // Rapid names already reported on this section (topos have no rapid list).
      rapidSuggestions() {
        const names = this.dangers.map((danger) => (danger.rapid_name || "").trim()).filter(Boolean);
        return [...new Set(names)].sort((a, b) => a.localeCompare(b, "fr"));
      },
    },
    methods: {
      dateToIsoDate,
      isoDateToDate,
      canEdit(danger) {
        const user = auth.currentUser;
        return this.isAdmin || Boolean(user && danger.created_by && danger.created_by.uid === user.uid);
      },
      openForm(danger) {
        this.editedDanger = danger;
        this.formOpen = true;
      },
      openCheckup(danger) {
        this.selectedDanger = danger;
        this.checkup = { date: dateToIsoDate(new Date()), comment: "", archive: false };
        this.checkupOpen = true;
      },
      openFlag(danger) {
        this.selectedDanger = danger;
        this.flagComment = "";
        this.flagOpen = true;
      },
      async saveCheckup() {
        const { valid } = await this.$refs.checkupForm.validate();
        if (!valid || this.saving) return;
        this.saving = true;
        try {
          const user = auth.currentUser;
          await DangersService.actions.addCheckup(this.selectedDanger.id, this.checkup, user);
          if (this.checkup.archive) {
            await DangersService.actions.setArchived(this.selectedDanger.id, true, user);
          }
          notify(this.checkup.archive ? "Danger archivé." : "Merci! La vérification a été enregistrée.");
          this.checkupOpen = false;
          this.$emit("changed");
        } catch (error) {
          console.error("Error saving danger check-up:", error);
          notifyError("La vérification n'a pas pu être enregistrée.");
        } finally {
          this.saving = false;
        }
      },
      async unarchive(danger) {
        try {
          await DangersService.actions.setArchived(danger.id, false, auth.currentUser);
          notify("Danger réactivé.");
          this.$emit("changed");
        } catch (error) {
          console.error("Error reactivating danger:", error);
          notifyError("Le danger n'a pas pu être réactivé.");
        }
      },
      async saveFlag() {
        const { valid } = await this.$refs.flagForm.validate();
        if (!valid || this.saving) return;
        this.saving = true;
        try {
          await DangersService.actions.flagDanger(this.selectedDanger, this.flagComment, auth.currentUser);
          notify("Merci! Les administrateurs ont été avisés.");
          this.flagOpen = false;
        } catch (error) {
          console.error("Error flagging danger:", error);
          notifyError("Le signalement n'a pas pu être envoyé.");
        } finally {
          this.saving = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .danger-intro {
    min-width: 240px;
  }
</style>
